import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, fileDownloader } from '@unified-checkout/design-system';
import { Download, File } from '@unified-checkout/design-system/icons/index';
import { formatToLocalizedDateTime } from 'libs/design-system/src/utils/dateFormats';

import { GENERIC_TOASTER_MAX_WIDTH, TOASTER_SHARED_PROPS } from '../../../utils';
import { notify } from '@app/dashboard/utils/notifier';
import { getDocument } from '@app/dashboard/services/inner_layout/disputes/get-document';
import { useLangStore } from '@app/dashboard/store/lang';
import { LabeledBox } from '../../components/LabeledBox';
import styles from './uploaded-file-box.module.scss';


export const UploadedFileBox = ({
  label,
  attachments
}: {
  label?: string;
  attachments: Record<string, any>[];
}) => {
  const { lang } = useLangStore();
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState<number[]>([]);

  const handleFileDownload = async (attachment: Record<string, any>) => {
    const { url, original_filename: fileName, id } = attachment;
    const [, disputeId, attachmentId] = url.match(/\/disputes\/(\d+)\/attachments\/(\d+)/) ?? [];

    setIsDownloading(prev => [...prev, id]);

    try {
      const { data } = await getDocument(disputeId, attachmentId);
      const blobUrl = URL.createObjectURL(data);
      fileDownloader(blobUrl, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);

      notify.error(t('innerLayout.disputesPage.disputeDetails.detailsBoxes.uploadedFiles.failedToDownload'), {
        ...TOASTER_SHARED_PROPS,
        maxWidth: GENERIC_TOASTER_MAX_WIDTH
      });
    } finally {
      setIsDownloading(prev => prev.filter(v => v !== id));
    }
  };

  return (
    <div className={styles["uploaded-file-box"]}>
      <LabeledBox
        label={label}
      >
        {
          attachments.map((attachment, i) =>
            <div className={styles["uploaded-file-box__file-wrapper"]} key={i}>
              <div className={styles["uploaded-file-box__file-wrapper__file-details"]}>

                <div className={styles["uploaded-file-box__file-wrapper__file-details__icon-wrapper"]}>
                  {File}
                </div>

                <div className={styles["uploaded-file-box__file-wrapper__file-details__text-box"]}>

                  <div className={styles["uploaded-file-box__file-wrapper__file-details__text-box__file-name"]}>
                    {attachment.original_filename}
                  </div>

                  {
                    <div className={styles["uploaded-file-box__file-wrapper__file-details__text-box__hint"]}>
                      {formatToLocalizedDateTime(attachment.created_at as string, lang)}
                    </div>
                  }
                </div>

              </div>

              <Button
                size='sm'
                color="secondary"
                variant="outlined"
                leadingIcon={Download}
                text={t('innerLayout.download')}
                onClickHandler={() => handleFileDownload(attachment)}
                disabled={isDownloading.includes(attachment.id)}
              />
            </div>
          )
        }
      </LabeledBox>

    </div>
  )
}
